import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTicketPermissions } from '../../hooks/useTicketPermissions';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { buildAssignmentOnlyPayload } from '../../utils/ticketAssignmentEdit';
import StateTransitionModal from '../../components/StateTransitionModal';
import ActivityTab from '../../components/ActivityTab';
import CaseProgressFlow from '../../components/CaseProgressFlow';
import AttachmentTab from '../../components/AttachmentTab';
import TabLoadingSpinner from '../../components/TabLoadingSpinner';
import RecordTimeModal from '../../components/RecordTimeModal';
import AssignmentGroupChangeConfirmModal from '../../components/AssignmentGroupChangeConfirmModal';
import BreachReasonModal from '../../components/BreachReasonModal';
import RichTextEditor from '../../components/RichTextEditor';
import RichTextDisplay from '../../components/RichTextDisplay';
import ResizableTextDisplay from '../../components/ResizableTextDisplay';
import TicketAccessDenied from '../../components/TicketAccessDenied';
import AccessLostModal from '../../components/AccessLostModal';
import SlaInstancesDisplay, { areAllSlaInstancesTerminal } from '../../components/SlaInstancesDisplay';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import DateTimePickerInput from '../../components/DateTimePickerInput';
import { trackCaseOperation } from '../../utils/appInsights';
import {
  impactLabels,
  urgencyLabels,
  priorityLabels
} from '../../utils/labelMappings';
import { sanitizeHtml, sanitizeNoteInput, hasActualTextContent, stripHtml } from '../../utils/sanitize';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';
import { SreInvestigateActions } from '../../features/sre-investigation';
import { resolveTicketAlertSource } from '../../features/sre-investigation/utils/ticketIdentity';
import {
  ArrowLeftIcon,
  PencilIcon,
  UserIcon,
  ClockIcon,
  CheckIcon,
  ChatBubbleLeftRightIcon,
  BriefcaseIcon,
  ExclamationCircleIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  LightBulbIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  TicketIcon
} from '@heroicons/react/24/outline';

interface WorkNote {
  id: string;
  User?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  note: string;
  timestamp: string;
  isPublic: boolean;
}

interface Case {
  _id: string;
  id?: string;
  number: string;
  shortDescription: string;
  description?: string;
  category: string;
  priority: string;
  state: string;
  caseType?: string;
  channel: string;
  impact: string;
  urgency: string;
  customerId?: string;
  engagementId?: string;
  servicePlanId?: string;
  resourceId?: string;
  resourceIdValue?: string;
  otherResourceNames?: string;
  assignmentGroupId?: string;
  contactEmails?: string[];
  contactId?: string;
  contact?: {
    _id?: string;
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  createdBy?: {
    _id?: string;
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  createdById?: string;
  assignedToId?: string;
  assignedTo?: {
    _id?: string;
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  Customer?: {
    id: string;
    accountName: string;
    accountShortName?: string;
  };
  Engagement?: {
    id: string;
    name: string;
    status: string;
  };
  ServicePlan?: {
    id: string;
    name: string;
  };
  Resource?: {
    id: string;
    name: string;
    resourceId?: string;
    resourceGroupName?: string;
  };
  AssignmentGroup?: {
    id: string;
    name: string;
  };
  ReferenceAlert?: {
    id: string;
    number: string;
    shortDescription: string;
    state: string;
    severity?: string;
    priority?: string;
    node?: string;
    source?: string;
  };
  Alert?: Array<{
    source?: string | null;
    sourceService?: string | null;
    sourceSubService?: string | null;
  }>;
  workNotes?: WorkNote[];
  resolution?: string;
  resolutionNotes?: string;
  closureNotes?: string;
  resolutionCode?: string;
  cause?: string;
  resolvedById?: string;
  closedById?: string;
  resolvedBy?: {
    _id?: string;
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  closedBy?: {
    _id?: string;
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  closedAt?: string;
  breachReason?: string;
  breachReasonProvidedAt?: string;
  responseBreachReason?: string;
  ongoingBreachReason?: string;
  resolutionBreachReason?: string;
  /** SRE investigation gate status on Case (T-021 / T-022). */
  sreInvestigationStatus?: string | null;
  sreDecisionReason?: string | null;
  sreEvaluatedAt?: string | null;
  sreRunId?: string | null;
  sreDecisionId?: string | null;
  sreUseCaseId?: string | null;
}

const priorityColors = {
  CRITICAL: 'bg-red-100 text-red-800 border-red-200',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
  NORMAL: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  MEDIUM: 'bg-blue-100 text-blue-800 border-blue-200',
  LOW: 'bg-gray-100 text-gray-800 border-gray-200',
  // Support numeric values for backward compatibility
  1: 'bg-red-100 text-red-800 border-red-200',
  2: 'bg-orange-100 text-orange-800 border-orange-200',
  3: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  4: 'bg-blue-100 text-blue-800 border-blue-200',
  5: 'bg-gray-100 text-gray-800 border-gray-200',
  // Support matrix-based priority format (matches backend enums)
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200'
};

// Label mappings are now imported from centralized utility: impactLabels, urgencyLabels, priorityLabels, caseStateLabels

const categoryIcons: Record<string, React.ComponentType<any>> = {
  'Question': ChatBubbleLeftRightIcon,
  'Request': BriefcaseIcon,
  'Problem': ExclamationCircleIcon,
  'Complaint': ExclamationCircleIcon,
  'Feature Request': BriefcaseIcon,
  'Documentation': ClockIcon,
  'Training': UserIcon,
  'Other': ClockIcon
};

const workflowSteps = [
  { id: 'new', name: 'New', color: 'bg-blue-100 text-blue-800' },
  { id: 'open', name: 'Open', color: 'bg-blue-100 text-blue-800' },
  { id: 'awaiting_info', name: 'Awaiting Info', color: 'bg-purple-100 text-purple-800' },
  { id: 'on_hold', name: 'On Hold', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'solution_proposed', name: 'Solution Proposed', color: 'bg-teal-100 text-teal-800' },
  { id: 'in_progress', name: 'In Progress', color: 'bg-indigo-100 text-indigo-800' },
  { id: 'work_in_progress', name: 'Work in Progress', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'pending_customer', name: 'Pending Customer', color: 'bg-purple-100 text-purple-800' },
  { id: 'pending_vendor', name: 'Pending Vendor', color: 'bg-orange-100 text-orange-800' },
  { id: 'pending_approval', name: 'Pending Approval', color: 'bg-amber-100 text-amber-800' },
  { id: 'approved', name: 'Approved', color: 'bg-lime-100 text-lime-800' },
  { id: 'resolved', name: 'Resolved', color: 'bg-green-100 text-green-800' },
  { id: 'closed', name: 'Closed', color: 'bg-gray-100 text-gray-800' },
  { id: 'closed_complete', name: 'Closed Complete', color: 'bg-gray-100 text-gray-800' },
  { id: 'closed_incomplete', name: 'Closed Incomplete', color: 'bg-slate-100 text-slate-800' },
  { id: 'cancelled', name: 'Cancelled', color: 'bg-red-100 text-red-800' },
  { id: 'skipped', name: 'Skipped', color: 'bg-gray-100 text-gray-800' },
  { id: 'auto_resolved', name: 'Auto Resolved', color: 'bg-emerald-100 text-emerald-800' }
];

// SLA functionality has been removed

// Priority calculation function (matches backend logic)
const calculatePriorityFromImpactUrgency = (impact: string | undefined, urgency: string | undefined): string => {
  if (!impact || !urgency) return 'low_4'; // Default to Low

  const impactNum = parseInt(impact.split('_')[1]);
  const urgencyNum = parseInt(urgency.split('_')[1]);

  if (impactNum === 1 && urgencyNum === 1) return 'critical_1';
  if ((impactNum === 1 && urgencyNum === 2) || (impactNum === 2 && urgencyNum === 1)) return 'high_2';
  if ((impactNum === 1 && urgencyNum === 3) || (impactNum === 2 && urgencyNum === 2) || (impactNum === 3 && urgencyNum === 1)) return 'moderate_3';
  if ((impactNum === 2 && urgencyNum === 3) || (impactNum === 3 && urgencyNum === 2)) return 'low_4';
  if (impactNum === 3 && urgencyNum === 3) return 'planning_5';

  return 'low_4'; // Default to Low
};

interface CaseEditForm {
  shortDescription?: string;
  description?: string;
  category?: string;
  channel?: string;
  impact?: string;
  urgency?: string;
  state?: string;
  resolutionNotes?: string;
  resolutionCode?: string;
  assignedTo?: string | null;
  contactId?: string | null;
  createdById?: string | null;
  customerId?: string | null;
  engagementId?: string | null;
  servicePlanId?: string | null;
  resourceId?: string | null;
  otherResourceNames?: string | null;
  assignmentGroupId?: string | null;
}

const CaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDateTime } = useDateFormatter();
  const navigate = useNavigate();
  const location = useLocation();
  const casesListSearch = (location.state as { casesListSearch?: string } | null)?.casesListSearch;

  const navigateBackToCases = () => {
    if (casesListSearch !== undefined) {
      navigate(`/cases${casesListSearch}`);
    } else {
      navigate('/cases');
    }
  };

  const queryClient = useQueryClient();
  const { user, isContact } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const hasLoggedView = useRef(false);
  const previousCaseStateRef = useRef<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<CaseEditForm>({});
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'work_note' | 'customer_note'>('work_note');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'details' | 'communication' | 'timeline' | 'activities' | 'attachments' | 'notes' | 'closure'>('details');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [servicePlanQuery, setServicePlanQuery] = useState('');
  const [selectedResourceIdValue, setSelectedResourceIdValue] = useState<string>('');
  const [isOtherResource, setIsOtherResource] = useState<boolean>(false);

  // State for case transition modal
  const [transitionModal, setTransitionModal] = useState<{
    isOpen: boolean;
    targetState: string;
    actionLabel: string;
  }>({
    isOpen: false,
    targetState: '',
    actionLabel: ''
  });
  // State for change type selection modal
  const [showChangeTypeModal, setShowChangeTypeModal] = useState(false);
  const [selectedChangeType, setSelectedChangeType] = useState<'standard' | 'normal' | 'emergency'>('normal');
  // State for problem conversion modal
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [problemDate, setProblemDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [reportedById, setReportedById] = useState<string>('');
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const createDropdownRef = useRef<HTMLDivElement>(null);

  // Record Time modal state
  const [recordTimeModalOpen, setRecordTimeModalOpen] = useState(false);

  // Access lost modal state
  const [accessLostModalOpen, setAccessLostModalOpen] = useState(false);

  // Breach reason modal state
  const [breachReasonModal, setBreachReasonModal] = useState<{
    isOpen: boolean;
    targetType?: 'RESPONSE' | 'ONGOING' | 'RESOLUTION';
  }>({
    isOpen: false,
    targetType: undefined
  });

  // Assignment group change confirmation modal state
  const [groupChangeConfirmModal, setGroupChangeConfirmModal] = useState<{
    isOpen: boolean;
    newGroupId: string | null;
    newGroupName: string;
    pendingChanges: CaseEditForm;
  }>({
    isOpen: false,
    newGroupId: null,
    newGroupName: '',
    pendingChanges: {}
  });

  // Conversion breach reason modal state
  const [conversionBreachModal, setConversionBreachModal] = useState<{
    isOpen: boolean;
    conversionType: 'incident' | 'problem' | 'change' | 'service-request' | null;
    breachReasons: {
      response?: string;
      ongoing?: string;
      resolution?: string;
    };
    currentStep: 'RESPONSE' | 'ONGOING' | 'RESOLUTION' | null;
    stepsNeeded: ('RESPONSE' | 'ONGOING' | 'RESOLUTION')[];
  }>({
    isOpen: false,
    conversionType: null,
    breachReasons: {},
    currentStep: null,
    stepsNeeded: []
  });

  // Reset local UI state when navigating to a different case (prevents stale edit form / tabs).
  useEffect(() => {
    hasLoggedView.current = false;
    previousCaseStateRef.current = null;
    setIsEditing(false);
    setEditForm({});
    setNewNote('');
    setNoteType('work_note');
    setErrors({});
    setActiveTab('details');
    setAssignmentGroupQuery('');
    setAssignedToQuery('');
    setContactQuery('');
    setResourceQuery('');
    setServicePlanQuery('');
    setSelectedResourceIdValue('');
    setIsOtherResource(false);
    setTransitionModal({ isOpen: false, targetState: '', actionLabel: '' });
    setShowChangeTypeModal(false);
    setShowProblemModal(false);
    setRecordTimeModalOpen(false);
    setAccessLostModalOpen(false);
    setBreachReasonModal({ isOpen: false, targetType: undefined });
    setGroupChangeConfirmModal({
      isOpen: false,
      newGroupId: null,
      newGroupName: '',
      pendingChanges: {},
    });
    setConversionBreachModal({
      isOpen: false,
      conversionType: null,
      breachReasons: {},
      currentStep: null,
      stepsNeeded: [],
    });
    if (id) {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
    }
  }, [id, queryClient]);

  const { data: caseData, isLoading, error } = useQuery<Case>({
    queryKey: ['case', id],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}`);
      return response.data;
    },
    staleTime: 0, // Always consider data stale to ensure fresh fetch on navigation
    refetchOnMount: 'always' // Always refetch when navigating between cases
  });

  const { data: workNotes = [], isLoading: isLoadingNotes } = useQuery<WorkNote[]>({
    queryKey: ['case', id, 'notes'],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}/work-notes`);
      return response.data;
    },
    enabled: !!id && (activeTab === 'communication' || activeTab === 'timeline' || activeTab === 'notes'),
  });

  // Get ticket permissions based on backend access control and user role
  const { canEdit, canEditAssignment, isAssignmentOnlyEditMode, canChangeState, canAddWorkNote, canAddAttachments, editAccessReason } = useTicketPermissions(
    caseData,
    'cases'
  );

  // Log "Viewed Case Details" when case is loaded
  useEffect(() => {
    if (caseData && user && !hasLoggedView.current) {
      hasLoggedView.current = true;
      previousCaseStateRef.current = caseData.state || null;
      trackCaseOperation(
        'Case Details',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Viewed Case Details',
          caseNumber: caseData.number || caseData.id || id || 'Unknown',
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
    }
  }, [caseData, user, id, isServiceDeskUser]);

  // Update previous state when case state changes
  useEffect(() => {
    if (caseData?.state) {
      previousCaseStateRef.current = caseData.state;
    }
  }, [caseData?.state]);


  // Fetch contacts filtered by customer for the Contact dropdown in edit mode
  const { data: contactsData, isLoading: isLoadingContacts, refetch: refetchContacts } = useQuery({
    queryKey: ['contacts', caseData?.customerId || caseData?.Customer?.id],
    queryFn: async () => {
      const customerId = caseData?.customerId || caseData?.Customer?.id;
      if (!customerId) return { users: [] };
      const params = new URLSearchParams({
        class: 'contact',
        customerId: customerId,
        limit: '100'
      });
      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    },
    enabled: (isEditing || showProblemModal) && !!caseData && !!(caseData?.customerId || caseData?.Customer?.id)
  });

  // Fetch resources filtered by engagement for the Resource dropdown in edit mode
  const { data: resourcesData } = useQuery({
    queryKey: ['resources', caseData?.engagementId || caseData?.Engagement?.id],
    queryFn: async () => {
      const engagementId = caseData?.engagementId || caseData?.Engagement?.id;
      if (!engagementId) return { resources: [] };
      const response = await api.get(`/resources?engagementId=${engagementId}`);
      return response.data;
    },
    enabled: isEditing && !!caseData && !!(caseData?.engagementId || caseData?.Engagement?.id)
  });

  // Fetch service plans filtered by engagement for the Service Plan dropdown in edit mode
  const { data: servicePlansData } = useQuery({
    queryKey: ['service-plans', caseData?.engagementId || caseData?.Engagement?.id],
    queryFn: async () => {
      const engagementId = caseData?.engagementId || caseData?.Engagement?.id;
      if (!engagementId) return { plans: [] };
      const response = await api.get(`/engagements/${engagementId}/service-plans`);
      return response.data;
    },
    enabled: isEditing && !!caseData && !!(caseData?.engagementId || caseData?.Engagement?.id)
  });

  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: isEditing,
  });

  // State-change history for the details-tab progress flow. Activities tab reuses this cache.
  const { data: caseProgressActivities } = useQuery({
    queryKey: ['case-activities', id],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}/activities`);
      return response.data;
    },
    enabled: !!id,
  });

  const { data: groupMembersData } = useQuery({
    queryKey: ['group-members', editForm.assignmentGroupId, editForm.customerId || caseData?.customerId || caseData?.Customer?.id],
    queryFn: async () => {
      if (!editForm.assignmentGroupId) return [];
      return fetchAssignmentGroupMembers(
        editForm.assignmentGroupId,
        editForm.customerId || caseData?.customerId || caseData?.Customer?.id || null
      );
    },
    enabled: !!editForm.assignmentGroupId
  });

  // Fetch current assignment group members (for non-editing mode - "Assign To Me" button)
  const { data: currentGroupMembersData } = useQuery({
    queryKey: ['group-members', caseData?.assignmentGroupId, caseData?.customerId || caseData?.Customer?.id],
    queryFn: async () => {
      if (!caseData?.assignmentGroupId) return [];
      return fetchAssignmentGroupMembers(
        caseData.assignmentGroupId,
        caseData.customerId || caseData.Customer?.id || null
      );
    },
    enabled: !!caseData?.assignmentGroupId && !isEditing
  });

  const { data: slaInstances } = useQuery({
    queryKey: ['sla-instances', 'CASE', id],
    queryFn: async () => {
      const response = await api.get(`/sla-instances/CASE/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 0, // Always consider data stale to ensure fresh fetch on navigation
    refetchOnMount: 'always', // Always refetch when navigating between cases
    // Stop refetching when all SLAs are in terminal states (BREACHED, ACHIEVED, COMPLETED, CANCELLED)
    refetchInterval: (query) => {
      const data = query.state.data;
      if (areAllSlaInstancesTerminal(data)) {
        return false; // Stop polling
      }
      return 30000; // Continue polling every 30 seconds
    }
  });


  // Handle clicking outside to close the create dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target as Node)) {
        setIsCreateDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Refetch contacts when problem modal opens
  useEffect(() => {
    if (showProblemModal && caseData) {
      console.log('[CaseDetail] Problem modal opened, refetching contacts');
      console.log('[CaseDetail] Case engagementId:', caseData.engagementId);
      console.log('[CaseDetail] Case Engagement?.id:', caseData.Engagement?.id);
      refetchContacts();
    }
  }, [showProblemModal, caseData, refetchContacts]);

  const updateCaseMutation = useMutation({
    mutationFn: async (data: CaseEditForm) => {
      const response = await api.put(`/cases/${id}`, data);
      return response.data;
    },
    onSuccess: (updated: any) => {
      // Update cache with new data to prevent error when access is lost
      queryClient.setQueryData(['case', id], updated);

      // Check if this was a confirmed assignment group change - if so, redirect to list
      if (groupChangeConfirmModal.isOpen) {
        setGroupChangeConfirmModal({
          isOpen: false,
          newGroupId: null,
          newGroupName: '',
          pendingChanges: {}
        });
        toast.success('Case updated successfully. You no longer have access to this case.');
        queryClient.invalidateQueries({ queryKey: ['cases'] });
        navigateBackToCases();
        return;
      }

      // Check if user lost access after the update (Service Desk users retain view access)
      if (updated.canEdit === false && !isServiceDeskUser && !updated.canEditAssignment) {
        setAccessLostModalOpen(true);
        return;
      }

      // Log Case Details Modified
      if (user) {
        const caseNumber = updated?.case?.number || updated?.number || caseData?.number || caseData?.id || id || 'Unknown';
        trackCaseOperation(
          'Case Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Case Details Modified',
            caseNumber: caseNumber,
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
          }
        );
      }

      queryClient.invalidateQueries({ queryKey: ['case', id] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      setIsEditing(false);
      setErrors({});
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update case';
      setErrors({ submit: message });
    }
  });

  const changeStatusMutation = useMutation({
    mutationFn: async (newState: string) => {
      // Make the PUT request and then immediately fetch fresh data
      await api.put(`/cases/${id}`, { state: newState });
      // Fetch fresh case data to return with proper field mapping
      const freshData = await api.get(`/cases/${id}`);
      return freshData.data;
    },
    onSuccess: (updated: any) => {
      // Check if user lost access after the update
      if (updated.canEdit === false) {
        queryClient.setQueryData(['case', id], updated);
        setAccessLostModalOpen(true);
        return;
      }

      // Update cache with fresh data that has proper field mapping
      queryClient.setQueryData(['case', id], updated);
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to change status';
      setErrors({ status: message });
    }
  });

  const addNoteMutation = useMutation({
    mutationFn: async (data: { note: string; isPublic: boolean; noteType: 'work_note' | 'customer_note' }) => {
      const response = await api.post(`/cases/${id}/work-notes`, {
        note: data.note,
        isPublic: data.isPublic
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      queryClient.invalidateQueries({ queryKey: ['case', id, 'notes'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      setNewNote('');
      
      // Log Note Added - distinguish between Work Note and Customer Note
      if (user && caseData) {
        const action = variables.noteType === 'customer_note' ? 'Customer Note Added' : 'Work Note Added';
        trackCaseOperation(
          'Case Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: action,
            caseNumber: caseData.number || caseData.id || id || 'Unknown',
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
    }
  });

  // Breach reason mutation
  const breachReasonMutation = useMutation({
    mutationFn: async ({ breachReason, targetType }: { breachReason: string; targetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION' }) => {
      const response = await api.put(`/cases/${id}/breach-reason`, { breachReason, targetType });
      return response.data;
    },
    onSuccess: async () => {
      // Use refetchQueries to ensure data is fresh before closing modal
      await queryClient.refetchQueries({ queryKey: ['case', id] });
      // Refetch list queries so breach reason shows without browser refresh
      queryClient.refetchQueries({ queryKey: ['cases'] });
      toast.success('Breach reason saved successfully');
      setBreachReasonModal({ isOpen: false, targetType: undefined });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to save breach reason';
      toast.error(message);
    }
  });

  // Computed values for SLA breach status
  const hasSlaBreached = React.useMemo(() => {
    // Check case's direct slaBreached field first
    if ((caseData as any)?.slaBreached) return true;
    // Also check SLA instances
    if (!slaInstances) return false;
    return slaInstances.some((instance: any) =>
      instance.currentState === 'BREACHED' || instance.isBreached
    );
  }, [(caseData as any)?.slaBreached, slaInstances]);

  // Check if breach reasons are provided for all breached SLAs
  const breachReasons = {
    response: caseData?.responseBreachReason,
    ongoing: caseData?.ongoingBreachReason,
    resolution: caseData?.resolutionBreachReason
  };

  // Helper to check if a specific SLA target type is breached
  const isSlaTargetBreached = (targetType: string): boolean => {
    if (!slaInstances) return false;
    return slaInstances.some(
      (instance: any) =>
        // Check both instance.targetType and instance.SlaPolicy.targetType for compatibility
        (instance.targetType === targetType || instance.SlaPolicy?.targetType === targetType) &&
        (instance.currentState === 'BREACHED' || instance.isBreached)
    );
  };

  // Check if all breached SLAs have breach reasons
  const hasAllBreachReasons = (): boolean => {
    if (!hasSlaBreached) return true;

    // Check each breached SLA target type for its breach reason
    const responseBreached = isSlaTargetBreached('RESPONSE');
    const ongoingBreached = isSlaTargetBreached('ONGOING');
    const resolutionBreached = isSlaTargetBreached('RESOLUTION');

    if (responseBreached && !caseData?.responseBreachReason) return false;
    if (ongoingBreached && !caseData?.ongoingBreachReason) return false;
    if (resolutionBreached && !caseData?.resolutionBreachReason) return false;

    return true;
  };

  // Legacy check for backward compatibility
  const hasBreachReason = hasAllBreachReasons();

  // Get the list of SLA target types that need breach reasons for conversion
  const getMissingBreachReasonTargets = (): ('RESPONSE' | 'ONGOING' | 'RESOLUTION')[] => {
    if (!hasSlaBreached) return [];

    const missingTargets: ('RESPONSE' | 'ONGOING' | 'RESOLUTION')[] = [];

    if (isSlaTargetBreached('RESPONSE') && !caseData?.responseBreachReason) {
      missingTargets.push('RESPONSE');
    }
    if (isSlaTargetBreached('ONGOING') && !caseData?.ongoingBreachReason) {
      missingTargets.push('ONGOING');
    }
    if (isSlaTargetBreached('RESOLUTION') && !caseData?.resolutionBreachReason) {
      missingTargets.push('RESOLUTION');
    }

    return missingTargets;
  };

  // Initiate conversion with breach reason collection if needed
  const initiateConversion = (conversionType: 'incident' | 'problem' | 'change' | 'service-request') => {
    const missingTargets = getMissingBreachReasonTargets();

    if (missingTargets.length > 0) {
      // Need to collect breach reasons first
      setConversionBreachModal({
        isOpen: true,
        conversionType,
        breachReasons: {},
        currentStep: missingTargets[0],
        stepsNeeded: missingTargets
      });
    } else {
      // No breach reasons needed, proceed with conversion
      proceedWithConversion(conversionType, {});
    }
  };

  // Proceed with conversion after collecting breach reasons
  const proceedWithConversion = (
    conversionType: 'incident' | 'problem' | 'change' | 'service-request',
    breachReasons: { response?: string; ongoing?: string; resolution?: string }
  ) => {
    const formattedReasons = {
      responseBreachReason: breachReasons.response,
      ongoingBreachReason: breachReasons.ongoing,
      resolutionBreachReason: breachReasons.resolution
    };

    switch (conversionType) {
      case 'incident':
        convertToIncidentMutation.mutate(formattedReasons);
        break;
      case 'problem':
        // For problem, we need to show the problem modal first
        // Store the breach reasons to be used when the problem modal is submitted
        setConversionBreachModal(prev => ({
          ...prev,
          isOpen: false,
          breachReasons
        }));
        setShowProblemModal(true);
        break;
      case 'change':
        // For change, we need to show the change type modal first
        // Store the breach reasons to be used when the change type modal is submitted
        setConversionBreachModal(prev => ({
          ...prev,
          isOpen: false,
          breachReasons
        }));
        setShowChangeTypeModal(true);
        break;
      case 'service-request':
        convertToServiceRequestMutation.mutate(formattedReasons);
        break;
    }

    // Reset the modal state for incident and service-request (they proceed immediately)
    if (conversionType === 'incident' || conversionType === 'service-request') {
      setConversionBreachModal({
        isOpen: false,
        conversionType: null,
        breachReasons: {},
        currentStep: null,
        stepsNeeded: []
      });
    }
  };

  // Handle breach reason modal step completion
  const handleConversionBreachReasonSubmit = (reason: string) => {
    const { currentStep, stepsNeeded, breachReasons, conversionType } = conversionBreachModal;

    if (!currentStep || !conversionType) return;

    // Save the current step's reason
    const newBreachReasons = { ...breachReasons };
    if (currentStep === 'RESPONSE') {
      newBreachReasons.response = reason;
    } else if (currentStep === 'ONGOING') {
      newBreachReasons.ongoing = reason;
    } else if (currentStep === 'RESOLUTION') {
      newBreachReasons.resolution = reason;
    }

    // Find the next step
    const currentIndex = stepsNeeded.indexOf(currentStep);
    const nextStep = stepsNeeded[currentIndex + 1];

    if (nextStep) {
      // Move to next step
      setConversionBreachModal(prev => ({
        ...prev,
        breachReasons: newBreachReasons,
        currentStep: nextStep
      }));
    } else {
      // All steps completed, proceed with conversion
      proceedWithConversion(conversionType, newBreachReasons);
    }
  };

  const assignCaseMutation = useMutation({
    mutationFn: async (data: { assignedTo?: string; assignmentGroup?: string }) => {
      const response = await api.put(`/cases/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      toast.success('Case assigned successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to assign case');
    }
  });

  const acknowledgeCaseMutation = useMutation({
    mutationFn: async () => {
      const response = await api.put(`/cases/${id}/acknowledge`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['case', id] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      
      // Log Case Acknowledge State Updated
      if (user && caseData) {
        const oldState = caseData.state || 'new';
        const newState = data?.case?.state || data?.state || 'open';
        trackCaseOperation(
          'Case Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Case Acknowledge State Updated',
            caseNumber: caseData.number || caseData.id || id || 'Unknown',
            oldState: oldState,
            newState: newState,
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
    }
  });

  const convertToIncidentMutation = useMutation({
    mutationFn: async (breachReasons?: { responseBreachReason?: string; ongoingBreachReason?: string; resolutionBreachReason?: string }) => {
      const response = await api.post(`/cases/${id}/convert-to-incident`, breachReasons || {});
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Successfully converted Case to Incident!');
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['incidents'] });
      
      // Log Converted Incident
      if (user && caseData) {
        const incidentNumber = data?.incident?.number || data?.number || id || 'Unknown';
        trackCaseOperation(
          'Case Details - Incident Converted',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Converted Incident',
            caseNumber: caseData.number || caseData.id || id || 'Unknown',
            incidentNumber: incidentNumber,
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
      
      // Navigate to the incident detail page using the case ID (since it's now an incident with the same ID)
      navigate(`/incidents/${id}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to convert case to incident';
      toast.error(message);
    }
  });

  const convertToProblemMutation = useMutation({
    mutationFn: async (data: { problemDate: string; reportedById: string | null; responseBreachReason?: string; ongoingBreachReason?: string; resolutionBreachReason?: string }) => {
      const response = await api.post(`/cases/${id}/convert-to-problem`, data);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Successfully converted Case to Problem!');
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['problems'] });
      setShowProblemModal(false);
      
      // Log Converted to Problem
      if (user && caseData) {
        const problemNumber = data?.problem?.number || data?.number || id || 'Unknown';
        trackCaseOperation(
          'Case Details - Problem Converted',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Converted to Problem',
            caseNumber: caseData.number || caseData.id || id || 'Unknown',
            problemNumber: problemNumber,
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
      
      // Navigate to the problem detail page using the case ID (since it's now a problem with the same ID)
      navigate(`/problems/${id}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to convert case to problem';
      toast.error(message);
    }
  });

  const convertToChangeMutation = useMutation({
    mutationFn: async (data: { changeType: 'standard' | 'normal' | 'emergency'; responseBreachReason?: string; ongoingBreachReason?: string; resolutionBreachReason?: string }) => {
      const response = await api.post(`/cases/${id}/convert-to-change`, data);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Successfully converted Case to Change Request!');
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['changes'] });
      setShowChangeTypeModal(false);
      
      // Log Converted Change
      if (user && caseData) {
        const changeNumber = data?.change?.number || data?.number || id || 'Unknown';
        trackCaseOperation(
          'Case Details - Change Converted',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Converted Change',
            caseNumber: caseData.number || caseData.id || id || 'Unknown',
            changeNumber: changeNumber,
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
      
      // Navigate to the change detail page using the case ID (since it's now a change with the same ID)
      navigate(`/changes/${id}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to convert case to change request';
      toast.error(message);
      setShowChangeTypeModal(false);
    }
  });

  const convertToServiceRequestMutation = useMutation({
    mutationFn: async (breachReasons?: { responseBreachReason?: string; ongoingBreachReason?: string; resolutionBreachReason?: string }) => {
      const response = await api.post(`/cases/${id}/convert-to-service-request`, breachReasons || {});
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Successfully converted Case to Service Request!');
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['service-requests'] });
      
      // Log Converted to Service Request
      if (user && caseData) {
        const serviceRequestNumber = data?.serviceRequest?.number || data?.case?.number || data?.number || id || 'Unknown';
        trackCaseOperation(
          'Case Details - Service Request Converted',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Converted to Service Request',
            caseNumber: caseData.number || caseData.id || id || 'Unknown',
            serviceRequestNumber: serviceRequestNumber,
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
      
      // Navigate to the service request detail page using the case ID (since it's now a service request with the same ID)
      navigate(`/service-requests/${id}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to convert case to service request';
      toast.error(message);
    }
  });

  // Helper functions for state transitions
  const openTransitionModal = (targetState: string, actionLabel: string) => {
    // Check for breach reasons before allowing resolved or closed transitions
    if ((targetState === 'resolved' || targetState === 'closed') && hasSlaBreached && !hasBreachReason) {
      // Find which specific SLA target type is missing its breach reason
      let missingTargetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION' | null = null;

      if (isSlaTargetBreached('RESPONSE') && !caseData?.responseBreachReason) {
        missingTargetType = 'RESPONSE';
      } else if (isSlaTargetBreached('ONGOING') && !caseData?.ongoingBreachReason) {
        missingTargetType = 'ONGOING';
      } else if (isSlaTargetBreached('RESOLUTION') && !caseData?.resolutionBreachReason) {
        missingTargetType = 'RESOLUTION';
      }

      if (missingTargetType) {
        const targetLabel = missingTargetType.charAt(0) + missingTargetType.slice(1).toLowerCase();
        toast.error(`Please provide a breach reason for the ${targetLabel} SLA before resolving or closing this ticket.`);
        setBreachReasonModal({ isOpen: true, targetType: missingTargetType });
        return;
      }
    }

    setTransitionModal({
      isOpen: true,
      targetState,
      actionLabel
    });
  };

  const closeTransitionModal = () => {
    setTransitionModal({
      isOpen: false,
      targetState: '',
      actionLabel: ''
    });
  };

  const handleTransitionSuccess = (updatedCase: any) => {
    // Log Case State Updated if state changed
    if (user && previousCaseStateRef.current && updatedCase?.state && previousCaseStateRef.current !== updatedCase.state) {
      trackCaseOperation(
        'Case Details - Edit',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Case State Updated',
          caseNumber: caseData?.number || updatedCase?.number || id || 'Unknown',
          oldState: previousCaseStateRef.current,
          newState: updatedCase.state,
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
      previousCaseStateRef.current = updatedCase.state;
    }

    // Invalidate to refetch full case data with relations (contact, AssignmentGroup, assignedTo)
    // Don't use setQueryData here because state-transition endpoint returns partial data
    queryClient.invalidateQueries({ queryKey: ['case', id] });
    queryClient.invalidateQueries({ queryKey: ['cases'] });
    queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
  };

  const handleAcknowledge = () => {
    acknowledgeCaseMutation.mutate();
  };

  // Get available state transitions based on current state
  const getAvailableTransitions = (currentState: string) => {
    const transitions = [];

    switch (currentState) {
      case 'new':
        transitions.push({
          state: 'open',
          label: 'Acknowledge',
          icon: CheckCircleIcon,
          action: handleAcknowledge,
          className: 'bg-green-600 hover:bg-green-700'
        });
        break;
      
      case 'open':
        transitions.push(
          {
            state: 'in_progress',
            label: 'Start Work',
            icon: PlayIcon,
            action: () => openTransitionModal('in_progress', 'Start Work'),
            className: 'bg-blue-600 hover:bg-blue-700'
          },
          {
            state: 'awaiting_info',
            label: 'Awaiting Info',
            icon: ExclamationCircleIcon,
            action: () => openTransitionModal('awaiting_info', 'Mark as Awaiting Info'),
            className: 'bg-yellow-600 hover:bg-yellow-700'
          },
          {
            state: 'on_hold',
            label: 'Put On Hold',
            icon: PauseIcon,
            action: () => openTransitionModal('on_hold', 'Put Case On Hold'),
            className: 'bg-orange-600 hover:bg-orange-700'
          },
          {
            state: 'solution_proposed',
            label: 'Propose Solution',
            icon: LightBulbIcon,
            action: () => openTransitionModal('solution_proposed', 'Propose Solution'),
            className: 'bg-purple-600 hover:bg-purple-700'
          },
          {
            state: 'closed',
            label: 'Close Case',
            icon: CheckCircleIcon,
            action: () => openTransitionModal('closed', 'Close Case'),
            className: 'bg-green-600 hover:bg-green-700'
          },
          {
            state: 'cancelled',
            label: 'Cancel',
            icon: StopIcon,
            action: () => openTransitionModal('cancelled', 'Cancel Case'),
            className: 'bg-red-600 hover:bg-red-700'
          }
        );
        break;

      case 'in_progress':
        transitions.push(
          {
            state: 'awaiting_info',
            label: 'Awaiting Info',
            icon: ExclamationCircleIcon,
            action: () => openTransitionModal('awaiting_info', 'Mark as Awaiting Info'),
            className: 'bg-yellow-600 hover:bg-yellow-700'
          },
          {
            state: 'on_hold',
            label: 'Put On Hold',
            icon: PauseIcon,
            action: () => openTransitionModal('on_hold', 'Put Case On Hold'),
            className: 'bg-orange-600 hover:bg-orange-700'
          },
          {
            state: 'solution_proposed',
            label: 'Propose Solution',
            icon: LightBulbIcon,
            action: () => openTransitionModal('solution_proposed', 'Propose Solution'),
            className: 'bg-purple-600 hover:bg-purple-700'
          },
          {
            state: 'closed',
            label: 'Close Case',
            icon: CheckCircleIcon,
            action: () => openTransitionModal('closed', 'Close Case'),
            className: 'bg-green-600 hover:bg-green-700'
          },
          {
            state: 'cancelled',
            label: 'Cancel',
            icon: StopIcon,
            action: () => openTransitionModal('cancelled', 'Cancel Case'),
            className: 'bg-red-600 hover:bg-red-700'
          }
        );
        break;

      case 'awaiting_info':
      case 'on_hold':
      case 'solution_proposed':
        transitions.push(
          {
            state: 'in_progress',
            label: 'Resume Work',
            icon: PlayIcon,
            action: () => openTransitionModal('in_progress', 'Resume Work'),
            className: 'bg-blue-600 hover:bg-blue-700'
          },
          {
            state: 'closed',
            label: 'Close Case',
            icon: CheckCircleIcon,
            action: () => openTransitionModal('closed', 'Close Case'),
            className: 'bg-green-600 hover:bg-green-700'
          },
          {
            state: 'cancelled',
            label: 'Cancel',
            icon: StopIcon,
            action: () => openTransitionModal('cancelled', 'Cancel Case'),
            className: 'bg-red-600 hover:bg-red-700'
          }
        );
        break;

      default:
        break;
    }

    return transitions;
  };

  // Render AccessLostModal at top level so it always shows when open
  if (accessLostModalOpen) {
    return (
      <>
        <AccessLostModal
          isOpen={accessLostModalOpen}
          onConfirm={() => {
            setAccessLostModalOpen(false);
            navigateBackToCases();
          }}
          ticketNumber={caseData?.number}
        />
      </>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <TicketAccessDenied
        ticketType="Case"
        ticketNumber={caseData?.number}
        listPageUrl={
          casesListSearch !== undefined ? `/cases${casesListSearch}` : '/cases'
        }
      />
    );
  }

  // Compute missing mandatory fields for workflow actions
  const getMissingMandatoryFields = () => {
    const missingFields: string[] = [];

    // Short Description is mandatory
    if (!caseData.shortDescription?.trim()) {
      missingFields.push('Short Description');
    }

    // Assignment Group is mandatory
    if (!caseData.assignmentGroupId && !caseData.AssignmentGroup) {
      missingFields.push('Assignment Group');
    }

    return missingFields;
  };

  const missingMandatoryFields = getMissingMandatoryFields();
  const hasMissingMandatoryFields = missingMandatoryFields.length > 0;

  const handleEdit = () => {
    // Check if current resource is "Others"
    const hasOtherResource = !caseData.resourceId && !!caseData.otherResourceNames;
    setIsOtherResource(hasOtherResource);
    setSelectedResourceIdValue(caseData.resourceIdValue || '');

    setEditForm({
      shortDescription: caseData.shortDescription,
      description: caseData.description,
      category: caseData.category,
      channel: caseData.channel || 'web',
      impact: caseData.impact,
      urgency: caseData.urgency,
      state: caseData.state,
      resolutionNotes: caseData.resolutionNotes,
      resolutionCode: caseData.resolutionCode,
      assignedTo: caseData.assignedTo?.id || caseData.assignedTo?._id || null,
      contactId: caseData.contact?.id || caseData.contact?._id || null,
      createdById: caseData.createdBy?.id || caseData.createdBy?._id || caseData.createdById || null,
      customerId: caseData.customerId,
      engagementId: caseData.engagementId,
      servicePlanId: caseData.servicePlanId,
      resourceId: caseData.resourceId,
      otherResourceNames: caseData.otherResourceNames || null,
      assignmentGroupId: caseData.assignmentGroupId
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    if (isAssignmentOnlyEditMode) {
      if (!editForm.assignmentGroupId) {
        setErrors({ assignmentGroupId: 'Assignment Group is required' });
        return;
      }

      setErrors({});
      updateCaseMutation.mutate(buildAssignmentOnlyPayload(editForm));
      return;
    }

    // Validate mandatory fields
    const validationErrors: Record<string, string> = {};

    // Short Description - editable field
    if (!editForm.shortDescription?.trim()) {
      validationErrors.shortDescription = 'Short Description is required';
    }

    // Resource validation
    if (!editForm.resourceId && !isOtherResource) {
      validationErrors.resourceId = editForm.engagementId
        ? 'Resource is required (select a resource or choose "Others")'
        : 'Please select an engagement first';
    }
    if (isOtherResource && !editForm.otherResourceNames?.trim()) {
      validationErrors.otherResourceNames = 'Please enter at least one resource name';
    }

    // If there are validation errors, show them and prevent save
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({});

    // Prepare payload - handle resource/otherResourceNames mutual exclusivity
    const payload = {
      ...editForm,
      resourceId: isOtherResource ? null : editForm.resourceId,
      otherResourceNames: isOtherResource ? editForm.otherResourceNames : null
    };

    // Check if assignment group is changing and user is not a member of the new group
    // Only check for regular users (user/agent roles), not for super_admin/admin/manager
    // Service Desk users can access all tickets, so skip the warning for them
    const userRole = user?.role;
    const isGlobalAccessRole = userRole === 'super_admin' || userRole === 'admin' || userRole === 'manager';

    const originalAssignmentGroupId = caseData?.assignmentGroupId || caseData?.AssignmentGroup?.id;
    if (editForm.assignmentGroupId && editForm.assignmentGroupId !== originalAssignmentGroupId && !isGlobalAccessRole && !isServiceDeskUser) {
      const newGroupId = editForm.assignmentGroupId;
      // Check if user is a member of the new group by checking group members
      // groupMembersData is already fetched for the selected assignment group
      const newGroupMembers = groupMembersData || [];
      const isUserMemberOfNewGroup = newGroupMembers.some((member: any) =>
        member.id === user?.id || member._id === user?.id
      );

      if (!isUserMemberOfNewGroup) {
        // Find the new group name
        const newGroup = (assignmentGroupsData || []).find((group: any) =>
          group.id === newGroupId || group._id === newGroupId
        );
        const newGroupName = newGroup?.name || 'Unknown Group';

        // Show confirmation modal instead of saving immediately
        setGroupChangeConfirmModal({
          isOpen: true,
          newGroupId,
          newGroupName,
          pendingChanges: payload
        });
        return;
      }
    }

    updateCaseMutation.mutate(payload);
  };

  // Handler for confirming assignment group change
  const handleConfirmGroupChange = () => {
    // Save the changes and redirect to list after successful save
    updateCaseMutation.mutate(groupChangeConfirmModal.pendingChanges);
    // Note: Navigation to list will happen in mutation's onSuccess
  };

  // Handler for cancelling assignment group change
  const handleCancelGroupChange = () => {
    setGroupChangeConfirmModal({
      isOpen: false,
      newGroupId: null,
      newGroupName: '',
      pendingChanges: {}
    });
  };

  const handleAddNote = (e: React.FormEvent) => {
    e.preventDefault();
    const sanitizedNote = sanitizeNoteInput(newNote);
    if (!hasActualTextContent(sanitizedNote)) return;

    addNoteMutation.mutate({
      note: sanitizedNote,
      isPublic: isContact() ? true : (noteType === 'customer_note'),  // Customer users always create public notes
      noteType: noteType  // Pass noteType for logging
    });
  };

  const handleAssignToMe = () => {
    if (user?.id) {
      assignCaseMutation.mutate({ assignedTo: user.id });
    }
  };

  // Check if "Assign To Me" button should be shown
  const shouldShowAssignToMeButton = () => {
    if (!user?.id || !caseData || isEditing) return false;

    // Don't show if already assigned to current user
    const currentAssignedToId = caseData.assignedToId || caseData.assignedTo?.id || caseData.assignedTo?._id;
    if (currentAssignedToId === user.id) return false;

    // Check if user is member of the assignment group
    const isUserInGroup = (currentGroupMembersData || []).some((member: any) =>
      member.id === user.id || member.userId === user.id || member._id === user.id
    );

    // Show button if user is in the group (don't check canEdit - users in the group can assign to themselves)
    return isUserInGroup;
  };

  const handleStatusChange = (newState: string) => {
    // Directly trigger the status change without a blocking confirm dialog. Any confirmation
    // logic should be implemented via a non-blocking UI pattern (modal, toast, etc.).
    changeStatusMutation.mutate(newState);
  };

  const getNextStates = (currentState: string) => {
    const transitions: Record<string, string[]> = {
      'new': ['work_in_progress', 'cancelled'],
      'work_in_progress': ['pending_customer', 'pending_vendor', 'resolved', 'cancelled'],
      'pending_customer': ['work_in_progress', 'resolved', 'cancelled'],
      'pending_vendor': ['work_in_progress', 'resolved', 'cancelled'],
      'resolved': ['closed', 'work_in_progress'],
      'closed': ['work_in_progress'],
      'cancelled': ['new']
    };
    return transitions[currentState] || [];
  };


  const CategoryIcon = categoryIcons[caseData.category || 'Other'] || ClockIcon;

  // SLA calculation functionality has been removed

  // Priority computation logic available if needed

  // Check if case is in a final state (non-editable)
  const isInFinalState = caseData && (caseData.state === 'closed' || caseData.state === 'cancelled' || caseData.state === 'resolved' || caseData.state === 'auto_resolved');

  // Create Request Dropdown Component
  const CreateRequestDropdown = () => {
    const createOptions = [
      {
        label: 'Convert to Incident',
        icon: ExclamationCircleIcon,
        action: 'convert-to-incident',
        description: 'Report a service disruption or issue'
      },
      {
        label: 'Convert to Problem',
        icon: ExclamationCircleIcon,
        action: 'convert-to-problem',
        description: 'Identify root cause of issues'
      },
      {
        label: 'Convert to Change Request',
        icon: BriefcaseIcon,
        action: 'convert-to-change',
        description: 'Request a change to services'
      },
      {
        label: 'Convert to Service Request',
        icon: BriefcaseIcon,
        action: 'convert-to-service-request',
        description: 'Request a service or information'
      }
    ];

    return (
      <div ref={createDropdownRef} className="relative inline-block text-left">
        <button
          type="button"
          onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)}
          className="inline-flex items-center justify-center p-2 border border-gray-300 rounded-md bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          aria-expanded={isCreateDropdownOpen}
          aria-haspopup="true"
        >
          <TicketIcon className="h-5 w-5" />
          <ChevronDownIcon className="ml-1 h-4 w-4" />
        </button>

        {isCreateDropdownOpen && (
          <div className="origin-top-right absolute right-0 mt-2 w-72 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
            <div className="py-1" role="menu" aria-orientation="vertical">
              {createOptions.map((option) => {
                const Icon = option.icon;
                const handleOptionClick = () => {
                  if (option.action === 'convert-to-incident') {
                    // Disable conversion if case is in final state
                    if (isInFinalState) {
                      toast.error('Cannot convert closed or cancelled cases to incidents');
                      setIsCreateDropdownOpen(false);
                      return;
                    }
                    setIsCreateDropdownOpen(false);
                    initiateConversion('incident');
                  } else if (option.action === 'convert-to-problem') {
                    // Disable conversion if case is in final state
                    if (isInFinalState) {
                      toast.error('Cannot convert closed or cancelled cases to problems');
                      setIsCreateDropdownOpen(false);
                      return;
                    }
                    setIsCreateDropdownOpen(false);
                    initiateConversion('problem');
                  } else if (option.action === 'convert-to-change') {
                    // Disable conversion if case is in final state
                    if (isInFinalState) {
                      toast.error('Cannot convert closed or cancelled cases to change requests');
                      setIsCreateDropdownOpen(false);
                      return;
                    }
                    setIsCreateDropdownOpen(false);
                    initiateConversion('change');
                  } else if (option.action === 'convert-to-service-request') {
                    // Disable conversion if case is in final state
                    if (isInFinalState) {
                      toast.error('Cannot convert closed or cancelled cases to service requests');
                      setIsCreateDropdownOpen(false);
                      return;
                    }
                    setIsCreateDropdownOpen(false);
                    initiateConversion('service-request');
                  }
                };
                
                return (
                  <button
                    key={option.action}
                    onClick={handleOptionClick}
                    disabled={(convertToIncidentMutation.isPending && option.action === 'convert-to-incident') || (convertToProblemMutation.isPending && option.action === 'convert-to-problem') || (convertToChangeMutation.isPending && option.action === 'convert-to-change') || (convertToServiceRequestMutation.isPending && option.action === 'convert-to-service-request')}
                    className="group flex items-start px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    role="menuitem"
                  >
                    <Icon className="flex-shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-500 mt-0.5" />
                    <div className="ml-3">
                      <div className="font-medium">
                        {(option.action === 'convert-to-incident' && convertToIncidentMutation.isPending) ||
                         (option.action === 'convert-to-problem' && convertToProblemMutation.isPending) ||
                         (option.action === 'convert-to-change' && convertToChangeMutation.isPending) ||
                         (option.action === 'convert-to-service-request' && convertToServiceRequestMutation.isPending)
                          ? 'Converting...'
                          : option.label
                        }
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={navigateBackToCases}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to Cases
        </button>

        {/* Record Time Button - Show when assigned to current user and state is not new/closed/cancelled/resolved/auto_resolved */}
        {caseData &&
         user &&
         (caseData.assignedTo?.id === user.id || caseData.assignedToId === user.id) &&
         caseData.state !== 'new' &&
         caseData.state !== 'closed' &&
         caseData.state !== 'cancelled' &&
         caseData.state !== 'resolved' &&
         caseData.state !== 'auto_resolved' &&
         caseData.Customer &&
         caseData.Engagement &&
         caseData.AssignmentGroup && (
          <button
            onClick={() => setRecordTimeModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <ClockIcon className="h-5 w-5 mr-2" />
            Record Time
          </button>
        )}
      </div>

      {/* Dynamic Workflow Progress */}
      <CaseProgressFlow 
        activities={caseProgressActivities || []} 
        currentState={caseData.state} 
      />

      {/* Case Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <CategoryIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {caseData.number}{caseData.shortDescription && <span className="text-base font-normal text-gray-600"> - {stripHtml(caseData.shortDescription)}</span>}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Created on {formatDateTime(caseData.createdAt)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${priorityColors[caseData.priority as keyof typeof priorityColors]}`}>
                Priority {priorityLabels[caseData.priority as keyof typeof priorityLabels]}
              </span>
              {shouldShowAssignToMeButton() && (
                <button
                  onClick={handleAssignToMe}
                  disabled={assignCaseMutation.isPending}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  {assignCaseMutation.isPending ? 'Assigning...' : 'Assign To Me'}
                </button>
              )}
              {!isEditing && (canEdit || canEditAssignment) && caseData?.state !== 'auto_resolved' && (
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  title={editAccessReason}
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Missing Mandatory Fields Warning */}
        {hasMissingMandatoryFields && caseData.state === 'new' && (
          <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
            <div className="flex items-start">
              <ExclamationCircleIcon className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  Missing Information - Cannot start workflow
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  The following mandatory fields are required before the case can be acknowledged: {' '}
                  <span className="font-medium">{missingMandatoryFields.join(', ')}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* State Transition Actions */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <div className="flex flex-wrap gap-2 items-center">
              {canChangeState && (
                <>
                  <span className="text-sm font-medium text-gray-700 mr-2">Available Actions:</span>
                  {getAvailableTransitions(caseData.state)
                    .filter((transition) => {
                      // Hide Acknowledge button if mandatory fields are missing
                      if (transition.label === 'Acknowledge' && hasMissingMandatoryFields) {
                        return false;
                      }
                      return true;
                    })
                    .map((transition) => {
                    const Icon = transition.icon;
                    return (
                      <button
                        key={transition.state}
                        onClick={transition.action}
                        className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white ${transition.className} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50`}
                        disabled={acknowledgeCaseMutation.isPending}
                      >
                        <Icon className="h-4 w-4 mr-1" />
                        {transition.label}
                      </button>
                    );
                  })}
                  {/* Show message when no actions available due to missing fields */}
                  {caseData.state === 'new' && hasMissingMandatoryFields && (
                    <span className="text-sm text-gray-500 italic">
                      Complete mandatory fields to enable workflow actions
                    </span>
                  )}
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 ml-auto">
              {canChangeState && caseData.state !== 'new' && caseData.state !== 'auto_resolved' && (
                <CreateRequestDropdown />
              )}
              <SreInvestigateActions
                ticketNumber={caseData.number}
                entityType="case"
                entityId={caseData.id || caseData._id}
                alertId={caseData.ReferenceAlert?.id}
                engagementId={caseData.engagementId || caseData.Engagement?.id}
                title={stripHtml(caseData.shortDescription) || undefined}
                description={typeof caseData.description === 'string' ? stripHtml(caseData.description) : undefined}
                node={caseData.ReferenceAlert?.node || caseData.Resource?.name || undefined}
                computer={caseData.ReferenceAlert?.node || caseData.Resource?.name || undefined}
                resourceId={
                  caseData.Resource?.resourceId ||
                  caseData.resourceIdValue ||
                  undefined
                }
                resourceGroup={caseData.Resource?.resourceGroupName || undefined}
                vmName={caseData.Resource?.name || undefined}
                ticketState={caseData.state}
                sreInvestigationStatus={caseData.sreInvestigationStatus}
                sreDecisionReason={caseData.sreDecisionReason}
                sreEvaluatedAt={caseData.sreEvaluatedAt}
                sreRunId={caseData.sreRunId}
                customerName={caseData.Customer?.accountName}
                engagementName={caseData.Engagement?.name}
                source={resolveTicketAlertSource(caseData)}
                onPosted={() => {
                  queryClient.invalidateQueries({ queryKey: ['case', id] });
                  queryClient.invalidateQueries({ queryKey: ['case', id, 'notes'] });
                }}
                forceShow
                autoStart={false}
              />
            </div>
          </div>
        </div>

        {/* SLA Timers */}
        <SlaInstancesDisplay
          slaInstances={slaInstances}
          onBreachDetected={() => {
            // Trigger immediate refetch when UI detects breach
            queryClient.invalidateQueries({ queryKey: ['sla-instances', 'CASE', id] });
          }}
          breachReasons={breachReasons}
          onRequestBreachReason={(targetType) => setBreachReasonModal({ isOpen: true, targetType })}
        />

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('communication')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'communication'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notes
            </button>
            <button
              onClick={() => setActiveTab('timeline')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'timeline'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Timeline
            </button>
            <button
              onClick={() => setActiveTab('activities')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'activities'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Activity
            </button>
            <button
              onClick={() => setActiveTab('attachments')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'attachments'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Attachments
            </button>
            {/* Only show Closure tab when case is resolved, closed, or auto_resolved */}
            {(caseData.state === 'resolved' || caseData.state === 'closed' || caseData.state === 'auto_resolved') && (
              <button
                onClick={() => setActiveTab('closure')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'closure'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Closure Information
              </button>
            )}
          </nav>
        </div>

        <div className="p-6">
          {errors.submit && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{errors.submit}</p>
            </div>
          )}

          {errors.status && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{errors.status}</p>
            </div>
          )}

          {/* Status Change Section */}
          {!isEditing && !isInFinalState && !isContact && canChangeState && (
            <div className="mb-6 bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700">Current Status:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    workflowSteps.find(step => step.id === caseData.state)?.color || 'bg-gray-100 text-gray-800'
                  }`}>
                    {workflowSteps.find(step => step.id === caseData.state)?.name || caseData.state}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">Change to:</span>
                  {getNextStates(caseData.state).map((stateId) => {
                    const stateInfo = workflowSteps.find(step => step.id === stateId);
                    return (
                      <button
                        key={stateId}
                        onClick={() => handleStatusChange(stateId)}
                        disabled={changeStatusMutation.isPending}
                        className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
                          stateId === 'resolved' ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' :
                          stateId === 'cancelled' ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' :
                          stateId === 'work_in_progress' ? 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100' :
                          stateId === 'pending_customer' ? 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100' :
                          stateId === 'closed' ? 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100' :
                          'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                        } disabled:opacity-50`}
                      >
                        {changeStatusMutation.isPending ? 'Updating...' : stateInfo?.name || stateId}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <>
              {isEditing && !isAssignmentOnlyEditMode ? (
              // ServiceNow-style Edit Layout
              <div className="space-y-6">
                {/* Row 1: Number (read-only) and State */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number</label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.number}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={(() => {
                            const stateLabels: Record<string, string> = {
                              'new': 'New',
                              'open': 'Open',
                              'in_progress': 'In Progress',
                              'work_in_progress': 'Work in Progress',
                              'awaiting_info': 'Awaiting Info',
                              'on_hold': 'On Hold',
                              'solution_proposed': 'Solution Proposed',
                              'pending_customer': 'Pending Customer',
                              'pending_vendor': 'Pending Vendor',
                              'resolved': 'Resolved',
                              'closed': 'Closed',
                              'cancelled': 'Cancelled'
                            };
                            return stateLabels[caseData?.state] || caseData?.state || 'New';
                          })()}
                          readOnly
                          disabled
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 2: Impact and Urgency */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
                      </div>
                      <div className="col-span-9">
                        <select
                          value={editForm.impact || 'low_3'}
                          onChange={(e) => setEditForm({ ...editForm, impact: e.target.value })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="high_1">1 - High</option>
                          <option value="medium_2">2 - Medium</option>
                          <option value="low_3">3 - Low</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Urgency</label>
                      </div>
                      <div className="col-span-9">
                        <select
                          value={editForm.urgency || 'low_3'}
                          onChange={(e) => setEditForm({ ...editForm, urgency: e.target.value })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="high_1">1 - High</option>
                          <option value="medium_2">2 - Medium</option>
                          <option value="low_3">3 - Low</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 3: Priority (computed) */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={(() => {
                            const priority = calculatePriorityFromImpactUrgency(editForm.impact, editForm.urgency);
                            const match = priority?.match(/_(\d)/);
                            const number = match ? match[1] : '';
                            const label = priorityLabels[priority as keyof typeof priorityLabels] || 'Low';
                            return number ? `${number} - ${label}` : label;
                          })()}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 4: Channel and Category */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Channel <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <select
                          value={editForm.channel || 'web'}
                          onChange={(e) => setEditForm({ ...editForm, channel: e.target.value })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="web">Web</option>
                          <option value="email">Email</option>
                          <option value="phone">Phone</option>
                          <option value="chat">Chat</option>
                          <option value="walk_in">Walk In</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <select
                          value={editForm.category || ''}
                          onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="question">Question</option>
                          <option value="issue">Issue</option>
                          <option value="request">Request</option>
                          <option value="change">Change</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 5: Customer and Engagement */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Customer <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.Customer?.accountName || '-'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Engagement <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.Engagement ? `${caseData.Engagement.name}${caseData.Engagement.status ? ` - ${caseData.Engagement.status}` : ''}` : '-'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 6: Contact and Service Plan */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.contactId || ''}
                          onChange={(value: string) => {
                            setEditForm({ ...editForm, contactId: value || null });
                            // Clear error when user selects a value
                            if (value && errors.contactId) {
                              setErrors(prev => {
                                const { contactId, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 ${
                                errors.contactId
                                  ? 'border-red-500 focus:ring-red-400'
                                  : 'border-gray-300 focus:ring-blue-400'
                              }`}
                              displayValue={(userId: string) => {
                                const user = (contactsData?.users || []).find((u: any) => u.id === userId);
                                return user ? `${user.firstName} ${user.lastName}` : '';
                              }}
                              onChange={(event) => setContactQuery(event.target.value)}
                              placeholder="Search contacts..."
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setContactQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(contactsData?.users || [])
                                  .filter((user: any) => `${user.firstName} ${user.lastName} ${user.email}`.toLowerCase().includes(contactQuery.toLowerCase()))
                                  .map((user: any) => (
                                  <Combobox.Option
                                    key={user.id}
                                    value={user.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{user.firstName} {user.lastName} ({user.email})</span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                        {errors.contactId && (
                          <p className="mt-1 text-sm text-red-600">{errors.contactId}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Service Plan</label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.servicePlanId || ''}
                          onChange={(value: string) => {
                            setEditForm({ ...editForm, servicePlanId: value || null });
                          }}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              displayValue={(servicePlanId: string) => {
                                const plan = (servicePlansData?.plans || []).find((p: any) => p.id === servicePlanId);
                                return plan ? plan.name : '';
                              }}
                              onChange={(e) => setServicePlanQuery(e.target.value)}
                              placeholder="Select Service Plan"
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setServicePlanQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(servicePlansData?.plans || [])
                                  .filter((plan: any) => plan && plan.id && plan.name && plan.name.toLowerCase().includes(servicePlanQuery.toLowerCase()))
                                  .map((plan: any) => (
                                    <Combobox.Option
                                      key={plan.id}
                                      value={plan.id}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                          active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {plan.name}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                              active ? 'text-white' : 'text-indigo-600'
                                            }`}>
                                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 7: Resource and Resource ID */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Resource <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={isOtherResource ? '__OTHER__' : (editForm.resourceId || '')}
                          onChange={(value: string) => {
                            if (value === '__OTHER__') {
                              setIsOtherResource(true);
                              setEditForm({ ...editForm, resourceId: null, otherResourceNames: '' });
                              setSelectedResourceIdValue('');
                            } else {
                              setIsOtherResource(false);
                              const selectedResource = (resourcesData?.resources || []).find((r: any) => r.id === value);
                              setSelectedResourceIdValue(selectedResource?.resourceId || '');
                              setEditForm({ ...editForm, resourceId: value || null, otherResourceNames: null });
                            }
                          }}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${errors.resourceId ? 'border-red-500' : 'border-gray-300'}`}
                              displayValue={(resourceId: string) => {
                                if (isOtherResource || resourceId === '__OTHER__') return 'Others (specify custom resources)';
                                const resource = (resourcesData?.resources || []).find((r: any) => r.id === resourceId);
                                return resource ? resource.name : '';
                              }}
                              onChange={(event) => setResourceQuery(event.target.value)}
                              placeholder="Search resources..."
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setResourceQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(resourcesData?.resources || [])
                                  .filter((resource: any) => resource.name.toLowerCase().includes(resourceQuery.toLowerCase()))
                                  .map((resource: any) => (
                                  <Combobox.Option
                                    key={resource.id}
                                    value={resource.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{resource.name}</span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                                {/* Others option at bottom */}
                                <Combobox.Option
                                  value="__OTHER__"
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 border-t border-gray-200 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        Others (specify custom resources)
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                        {errors.resourceId && <p className="mt-1 text-xs text-red-500">{errors.resourceId}</p>}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className={`grid grid-cols-12 gap-4 ${isOtherResource ? 'items-start' : 'items-end'}`}>
                      <div className="col-span-3">
                        <label className={`block text-sm font-medium text-gray-700 ${isOtherResource ? 'mt-1' : 'mb-1'}`}>
                          {isOtherResource ? 'Custom Resources' : 'Resource ID'}
                          {isOtherResource && <span className="text-red-500"> *</span>}
                        </label>
                      </div>
                      <div className="col-span-9">
                        {isOtherResource ? (
                          <>
                            <input
                              type="text"
                              value={editForm.otherResourceNames || ''}
                              onChange={(e) => setEditForm({ ...editForm, otherResourceNames: e.target.value })}
                              placeholder="Enter resource names, separated by commas (e.g., Server-01, Database-02)"
                              className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${errors.otherResourceNames ? 'border-red-500' : 'border-gray-300'}`}
                            />
                            {errors.otherResourceNames && <p className="mt-1 text-xs text-red-500">{errors.otherResourceNames}</p>}
                            <p className="mt-1 text-xs text-gray-500">Enter multiple resource names separated by commas</p>
                          </>
                        ) : (
                          <input
                            type="text"
                            value={(() => {
                              const selectedResource = (resourcesData?.resources || []).find((r: any) => r.id === editForm.resourceId);
                              return selectedResource?.resourceId || selectedResourceIdValue || caseData.resourceIdValue || '-';
                            })()}
                            readOnly
                            disabled
                            className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8 cursor-not-allowed"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 7.5: Reference Alert (if case was created from alert) */}
                {caseData.ReferenceAlert && (
                  <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Reference Alert</label>
                        </div>
                        <div className="col-span-9">
                          <Link
                            to={`/alerts/${caseData.ReferenceAlert.id}`}
                            className="inline-flex items-center px-3 py-1 border border-blue-300 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm h-8"
                          >
                            <TicketIcon className="h-4 w-4 mr-1" />
                            {caseData.ReferenceAlert.number}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Row 8: Assignment Group and Assigned To */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Group <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.assignmentGroupId || ''}
                          onChange={(value: string) => setEditForm({ ...editForm, assignmentGroupId: value || null, assignedTo: null })}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              displayValue={(groupId: string) => {
                                const group = (assignmentGroupsData || []).find((g: any) => g.id === groupId);
                                return group ? group.name : '';
                              }}
                              onChange={(event) => setAssignmentGroupQuery(event.target.value)}
                              placeholder="Search assignment groups..."
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setAssignmentGroupQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(assignmentGroupsData || [])
                                  .filter((group: any) => group.name.toLowerCase().includes(assignmentGroupQuery.toLowerCase()))
                                  .map((group: any) => (
                                  <Combobox.Option
                                    key={group.id}
                                    value={group.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{group.name}</span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.assignedTo || ''}
                          onChange={(value: string) => {
                            setEditForm({ ...editForm, assignedTo: value || null });
                            // Clear error when user selects a value
                            if (value && errors.assignedTo) {
                              setErrors(prev => {
                                const { assignedTo, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                          disabled={!editForm.assignmentGroupId}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 ${
                                errors.assignedTo
                                  ? 'border-red-500 focus:ring-red-400'
                                  : 'border-gray-300 focus:ring-blue-400'
                              } ${!editForm.assignmentGroupId ? 'bg-gray-50 text-gray-400' : ''}`}
                              displayValue={(userId: string) => {
                                const user = (groupMembersData || []).find((u: any) => u.id === userId);
                                return user ? `${user.firstName} ${user.lastName}` : '';
                              }}
                              onChange={(event) => setAssignedToQuery(event.target.value)}
                              placeholder={!editForm.assignmentGroupId ? 'Select group first' : 'Search users...'}
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setAssignedToQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(groupMembersData || [])
                                  .filter((member: any) => `${member.firstName} ${member.lastName}`.toLowerCase().includes(assignedToQuery.toLowerCase()))
                                  .map((member: any) => (
                                  <Combobox.Option
                                    key={member.id}
                                    value={member.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{member.firstName} {member.lastName}</span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                        {errors.assignedTo && (
                          <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 9: Short Description */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Short Description <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-10">
                        <input
                          type="text"
                          value={editForm.shortDescription || ''}
                          onChange={(e) => {
                            setEditForm({ ...editForm, shortDescription: e.target.value });
                            // Clear error when user types
                            if (e.target.value.trim() && errors.shortDescription) {
                              setErrors(prev => {
                                const { shortDescription, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                          className={`w-full px-3 py-1 border rounded text-sm h-8 ${
                            errors.shortDescription
                              ? 'border-red-500 focus:ring-red-400'
                              : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                          }`}
                        />
                        {errors.shortDescription && (
                          <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 10: Description */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      </div>
                      <div className="col-span-10">
                        <RichTextEditor
                          value={editForm.description || ''}
                          onChange={(value) => setEditForm({ ...editForm, description: value })}
                          placeholder="Enter case description"
                          minHeight="120px"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Resolution Notes field for resolved/closed states */}
                {(editForm.state === 'resolved' || editForm.state === 'closed') && (
                  <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-12">
                      <div className="grid grid-cols-12 gap-4 items-start">
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Resolution Notes</label>
                        </div>
                        <div className="col-span-10">
                          <RichTextEditor
                            value={editForm.resolutionNotes || ''}
                            onChange={(value) => setEditForm({ ...editForm, resolutionNotes: value })}
                            placeholder="Describe how the case was resolved"
                            minHeight="100px"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex justify-end space-x-2 mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={updateCaseMutation.isPending}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {updateCaseMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            ) : (
              // Read-only view layout matching Service Request style
              <div className="space-y-6">
                {isEditing && isAssignmentOnlyEditMode && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    You can update Assignment Group and Assigned To for this ticket. Other fields are read-only.
                  </div>
                )}
                {/* Row 1: Number and State */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number</label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.number}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={workflowSteps.find(step => step.id === caseData.state)?.name || caseData.state}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 2: Impact and Urgency */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.impact ? (impactLabels[caseData.impact] || caseData.impact) : '-'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Urgency</label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.urgency ? (urgencyLabels[caseData.urgency] || caseData.urgency) : '-'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 3: Priority */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={(() => {
                            const priority = caseData.priority || calculatePriorityFromImpactUrgency(caseData.impact, caseData.urgency);
                            const match = priority?.match(/_(\d)/);
                            const number = match ? match[1] : priority;
                            const label = priorityLabels[priority as keyof typeof priorityLabels];
                            return label ? `${number} - ${label}` : priority || '-';
                          })()}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 4: Channel and Category */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Channel <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.channel || '-'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.category ? caseData.category.charAt(0).toUpperCase() + caseData.category.slice(1) : '-'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 5: Customer and Engagement */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Customer <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.Customer?.accountName || '-'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Engagement <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.Engagement ? `${caseData.Engagement.name}${caseData.Engagement.status ? ` - ${caseData.Engagement.status}` : ''}` : '-'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 6: Contact and Service Plan */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.contact ? (`${caseData.contact.firstName || ''} ${caseData.contact.lastName || ''}`.trim() || caseData.contact.email || '-') : '-'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Service Plan</label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.ServicePlan?.name || '-'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 7: Resource and Resource ID */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Resource <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.otherResourceNames
                            ? 'Others (Custom)'
                            : (caseData.Resource?.name || '-')}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {caseData.otherResourceNames ? 'Resource Names' : 'Resource ID'}
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.otherResourceNames
                            ? caseData.otherResourceNames
                            : (caseData.resourceIdValue || caseData.Resource?.id || '-')}
                          readOnly
                          disabled
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 7.5: Reference Alert (if case was created from alert) */}
                {caseData.ReferenceAlert && (
                  <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Reference Alert</label>
                        </div>
                        <div className="col-span-9">
                          <Link
                            to={`/alerts/${caseData.ReferenceAlert.id}`}
                            className="inline-flex items-center px-3 py-1 border border-blue-300 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm h-8"
                          >
                            <TicketIcon className="h-4 w-4 mr-1" />
                            {caseData.ReferenceAlert.number}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Row 8: Assignment Group and Assigned To */}
                {isEditing && isAssignmentOnlyEditMode ? (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Group <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.assignmentGroupId || ''}
                          onChange={(value: string) => setEditForm({ ...editForm, assignmentGroupId: value || null, assignedTo: null })}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              displayValue={(groupId: string) => {
                                const group = (assignmentGroupsData || []).find((g: any) => g.id === groupId);
                                return group ? group.name : '';
                              }}
                              onChange={(event) => setAssignmentGroupQuery(event.target.value)}
                              placeholder="Search assignment groups..."
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setAssignmentGroupQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(assignmentGroupsData || [])
                                  .filter((group: any) => group.name.toLowerCase().includes(assignmentGroupQuery.toLowerCase()))
                                  .map((group: any) => (
                                  <Combobox.Option
                                    key={group.id}
                                    value={group.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{group.name}</span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.assignedTo || ''}
                          onChange={(value: string) => {
                            setEditForm({ ...editForm, assignedTo: value || null });
                            if (value && errors.assignedTo) {
                              setErrors(prev => {
                                const { assignedTo, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                          disabled={!editForm.assignmentGroupId}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 ${
                                errors.assignedTo
                                  ? 'border-red-500 focus:ring-red-400'
                                  : 'border-gray-300 focus:ring-blue-400'
                              } ${!editForm.assignmentGroupId ? 'bg-gray-50 text-gray-400' : ''}`}
                              displayValue={(userId: string) => {
                                const user = (groupMembersData || []).find((u: any) => u.id === userId);
                                return user ? `${user.firstName} ${user.lastName}` : '';
                              }}
                              onChange={(event) => setAssignedToQuery(event.target.value)}
                              placeholder={!editForm.assignmentGroupId ? 'Select group first' : 'Search users...'}
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setAssignedToQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(groupMembersData || [])
                                  .filter((member: any) => `${member.firstName} ${member.lastName}`.toLowerCase().includes(assignedToQuery.toLowerCase()))
                                  .map((member: any) => (
                                  <Combobox.Option
                                    key={member.id}
                                    value={member.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{member.firstName} {member.lastName}</span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                        {errors.assignedTo && (
                          <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                ) : (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Group <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={caseData.AssignmentGroup?.name || '-'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                      </div>
                      <div className="col-span-9 flex gap-2">
                        <input
                          type="text"
                          value={caseData.assignedTo ? (`${caseData.assignedTo.firstName || ''} ${caseData.assignedTo.lastName || ''}`.trim() || caseData.assignedTo.email || 'Unknown User') : '-'}
                          readOnly
                          className="flex-1 px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                        {shouldShowAssignToMeButton() && (
                          <button
                            onClick={handleAssignToMe}
                            disabled={assignCaseMutation.isPending}
                            className="px-3 py-1 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50 whitespace-nowrap h-8"
                          >
                            {assignCaseMutation.isPending ? 'Assigning...' : 'Assign To Me'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Row 9: Short Description */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">Short Description <span className="text-red-500">*</span></label>
                      </div>
                      <div className="col-span-10">
                        <ResizableTextDisplay
                          content={caseData.shortDescription || ''}
                          defaultHeight={60}
                          minHeight={40}
                          maxHeight={300}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 10: Description */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">Description</label>
                      </div>
                      <div className="col-span-10">
                        <ResizableTextDisplay
                          content={caseData.description || ''}
                          defaultHeight={120}
                          minHeight={60}
                          maxHeight={500}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Resolution section for resolved/closed/auto_resolved cases */}
                {(caseData.state === 'resolved' || caseData.state === 'closed' || caseData.state === 'auto_resolved') && caseData.resolutionNotes && (
                  <div className="bg-green-50 rounded-lg p-4 mt-6">
                    <h4 className="text-sm font-medium text-green-900">Resolution Notes</h4>
                    <div className="mt-2 text-sm text-green-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(caseData.resolutionNotes) }} />
                    {caseData.resolutionCode && (
                      <p className="mt-1 text-sm text-green-600">Code: {caseData.resolutionCode}</p>
                    )}
                    {caseData.resolvedAt && (
                      <p className="mt-1 text-xs text-green-600">
                        Resolved on {formatDateTime(caseData.resolvedAt)}
                      </p>
                    )}
                  </div>
                )}
                {isEditing && isAssignmentOnlyEditMode && (
                  <div className="flex justify-end space-x-2 mt-6 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={updateCaseMutation.isPending}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {updateCaseMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>
            )}
            </>
          )}

          {/* Notes Tab */}
          {activeTab === 'communication' && (
            <div>
              {/* Add Note Form */}
              {canAddWorkNote ? (
                <form onSubmit={handleAddNote} className="mb-6">
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700">Add Note</label>
                    <RichTextEditor
                      value={newNote}
                      onChange={setNewNote}
                      placeholder="Enter your note..."
                      minHeight="100px"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    {!isContact() ? (
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="work_note"
                            checked={noteType === 'work_note'}
                            onChange={(e) => setNoteType(e.target.value as 'work_note')}
                            className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">Work Note</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="customer_note"
                            checked={noteType === 'customer_note'}
                            onChange={(e) => setNoteType(e.target.value as 'customer_note')}
                            className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">Customer Note</span>
                        </label>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600 italic">Your notes will be visible to all users</p>
                    )}

                    <button
                      type="submit"
                      disabled={addNoteMutation.isPending || !newNote.trim()}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {addNoteMutation.isPending ? 'Adding...' : 'Add Note'}
                    </button>
                  </div>
                </form>
              ) : (
                isInFinalState ? (
                  <div className="mb-6 bg-gray-50 border border-gray-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-gray-700">Notes Locked</h3>
                        <div className="mt-2 text-sm text-gray-600">
                          <p>This case is {caseData?.state?.replace(/_/g, ' ')}. Notes cannot be added to closed, cancelled, resolved, or auto-resolved cases.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ExclamationCircleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">Cannot Add Notes</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>This ticket is not assigned to you. You cannot add notes until the ticket is assigned to you.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}

              {/* Notes List */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Work Notes</h3>
                {isLoadingNotes ? (
                  <TabLoadingSpinner label="Loading work notes" />
                ) : workNotes && workNotes.length > 0 ? (
                  <div className="space-y-4">
                    {workNotes.map((note: WorkNote) => (
                      <div key={note.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-600">
                                  {note.User?.firstName?.charAt(0).toUpperCase() || 'U'}
                                </span>
                              </div>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                {note.User ? `${note.User.firstName} ${note.User.lastName}` : 'Unknown User'}
                              </span>
                              <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                note.isPublic ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {note.isPublic ? 'Public' : 'Internal'}
                              </span>
                            </div>
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatDateTime(note.timestamp)}
                          </span>
                        </div>
                        <div className="ml-11">
                          <div className="text-sm text-gray-700">
                            <RichTextDisplay content={note.note} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">No work notes yet</p>
                    <p className="mt-1 text-xs text-gray-400">Work notes will appear here once added</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="flow-root">
              {isLoadingNotes ? (
                <TabLoadingSpinner label="Loading timeline" />
              ) : (
              <ul className="-mb-8">
                {/* Created Event */}
                <li>
                  <div className="relative pb-8">
                    <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center ring-8 ring-white">
                          <ClockIcon className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">Case created</p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          {formatDateTime(caseData.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                {/* Work Notes as Timeline Events */}
                {workNotes?.map((note: WorkNote, noteIdx: number) => (
                  <li key={note.id}>
                    <div className="relative pb-8">
                      {noteIdx !== (workNotes?.length || 0) - 1 && (
                        <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                      )}
                      <div className="relative flex space-x-3">
                        <div>
                          <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                            <ChatBubbleLeftRightIcon className="h-5 w-5 text-white" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5">
                          <div>
                            <p className="text-sm text-gray-500">
                              {note.User ? `${note.User.firstName} ${note.User.lastName}` : 'Unknown User'} added a {note.isPublic ? 'customer visible' : 'internal'} note
                            </p>
                            <div className="mt-1 text-sm text-gray-700">
                              <RichTextDisplay content={note.note} />
                            </div>
                          </div>
                          <div className="text-sm whitespace-nowrap text-gray-500 mt-1">
                            {formatDateTime(note.timestamp)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}

                {/* Resolved Event */}
                {caseData.resolvedAt && (
                  <li>
                    <div className="relative pb-8">
                      <div className="relative flex space-x-3">
                        <div>
                          <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                            <CheckIcon className="h-5 w-5 text-white" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                          <div>
                            <p className="text-sm text-gray-500">Case resolved</p>
                          </div>
                          <div className="text-right text-sm whitespace-nowrap text-gray-500">
                            {formatDateTime(caseData.resolvedAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                )}
              </ul>
              )}
            </div>
          )}

          {/* Activities Tab */}
          {activeTab === 'activities' && (
            <div className="bg-white shadow rounded-lg p-6">
              <ActivityTab entityId={id!} />
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && (
            <div className="bg-white shadow rounded-lg p-6">
              <AttachmentTab
                entityType="case"
                entityId={id!}
                canUpload={canAddAttachments}
                canDelete={canAddAttachments}
                ticketState={caseData?.state}
                onAttachmentAdded={() => {
                  if (user && caseData) {
                    trackCaseOperation(
                      'Case Details - Edit',
                      user.email || user.firstName || 'Unknown',
                      {
                        action: 'Added Attachments',
                        caseNumber: caseData.number || caseData.id || id || 'Unknown',
                      },
                      {
                        firstName: user.firstName,
                        lastName: user.lastName,
                        role: user.role,
                        roleType: user.roleType,
                        roleLevel: user.roleLevel,
                        userClass: user.class,
                        customerId: user.customerId,
                        customerName: user.customerName,
                        engagementId: user.engagementId,
                        engagementName: user.engagementName,
                        isServiceDeskUser: isServiceDeskUser,
                      }
                    );
                  }
                }}
                onAttachmentDeleted={() => {
                  if (user && caseData) {
                    trackCaseOperation(
                      'Case Details - Edit',
                      user.email || user.firstName || 'Unknown',
                      {
                        action: 'Removed Attachments',
                        caseNumber: caseData.number || caseData.id || id || 'Unknown',
                      },
                      {
                        firstName: user.firstName,
                        lastName: user.lastName,
                        role: user.role,
                        roleType: user.roleType,
                        roleLevel: user.roleLevel,
                        userClass: user.class,
                        customerId: user.customerId,
                        customerName: user.customerName,
                        engagementId: user.engagementId,
                        engagementName: user.engagementName,
                        isServiceDeskUser: isServiceDeskUser,
                      }
                    );
                  }
                }}
              />
            </div>
          )}

          {/* Closure Information Tab */}
          {activeTab === 'closure' && (caseData.state === 'resolved' || caseData.state === 'closed' || caseData.state === 'auto_resolved') && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Closure Information</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Resolution Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Resolution Code</label>
                    <p className="text-sm text-gray-900">
                      {caseData.resolutionCode ?
                        caseData.resolutionCode
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, (l: string) => l.toUpperCase())
                        : '-'}
                    </p>
                  </div>

                  {/* Resolved By */}
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Resolved By</label>
                    <p className="text-sm text-gray-900">
                      {caseData.resolvedBy ?
                        `${caseData.resolvedBy.firstName || ''} ${caseData.resolvedBy.lastName || ''}`.trim() || caseData.resolvedBy.email || '-'
                        : '-'}
                    </p>
                  </div>

                  {/* Resolved At */}
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Resolved At</label>
                    <p className="text-sm text-gray-900">
                      {caseData.resolvedAt ? formatDateTime(caseData.resolvedAt) : '-'}
                    </p>
                  </div>

                  {/* Closed By */}
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Closed By</label>
                    <p className="text-sm text-gray-900">
                      {caseData.closedBy ?
                        `${caseData.closedBy.firstName || ''} ${caseData.closedBy.lastName || ''}`.trim() || caseData.closedBy.email || '-'
                        : '-'}
                    </p>
                  </div>

                  {/* Closed At */}
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Closed At</label>
                    <p className="text-sm text-gray-900">
                      {caseData.closedAt ? formatDateTime(caseData.closedAt) : '-'}
                    </p>
                  </div>
                </div>

                {/* Cause */}
                {caseData.cause && (
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-500 mb-1">Cause</label>
                    <div className="mt-1 p-4 bg-gray-50 rounded-md">
                      <RichTextDisplay content={caseData.cause} />
                    </div>
                  </div>
                )}

                {/* Resolution Notes */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Resolution Notes</label>
                  {caseData.resolutionNotes ? (
                    <div className="mt-1 p-4 bg-green-50 border border-green-200 rounded-md">
                      <RichTextDisplay content={caseData.resolutionNotes} />
                    </div>
                  ) : (
                    <div className="mt-1 p-4 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-500 italic">
                      No resolution notes provided
                    </div>
                  )}
                </div>

                {/* Closure Notes */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Closure Notes</label>
                  {caseData.closureNotes ? (
                    <div className="mt-1 p-4 bg-gray-50 border border-gray-200 rounded-md">
                      <RichTextDisplay content={caseData.closureNotes} />
                    </div>
                  ) : (
                    <div className="mt-1 p-4 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-500 italic">
                      No closure notes provided
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* State Transition Modal */}
      {transitionModal.isOpen && (
        <StateTransitionModal
          isOpen={transitionModal.isOpen}
          onClose={closeTransitionModal}
          onSuccess={handleTransitionSuccess}
          caseId={caseData.id || caseData._id}
          currentState={caseData.state}
          targetState={transitionModal.targetState}
          actionLabel={transitionModal.actionLabel}
        />
      )}

      {/* Change Type Selection Modal */}
      <Transition appear show={showChangeTypeModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowChangeTypeModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 mb-4"
                  >
                    Select Change Type
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-4">
                      Choose the type of change request you want to create:
                    </p>
                    <div className="space-y-3">
                      <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <input
                          type="radio"
                          name="changeType"
                          value="standard"
                          checked={selectedChangeType === 'standard'}
                          onChange={(e) => setSelectedChangeType(e.target.value as 'standard' | 'normal' | 'emergency')}
                          className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">Standard Change</div>
                          <div className="text-xs text-gray-500 mt-1">Pre-approved, low-risk changes that follow established procedures</div>
                        </div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <input
                          type="radio"
                          name="changeType"
                          value="normal"
                          checked={selectedChangeType === 'normal'}
                          onChange={(e) => setSelectedChangeType(e.target.value as 'standard' | 'normal' | 'emergency')}
                          className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">Normal Change</div>
                          <div className="text-xs text-gray-500 mt-1">Requires CAB approval and full change management process</div>
                        </div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                        <input
                          type="radio"
                          name="changeType"
                          value="emergency"
                          checked={selectedChangeType === 'emergency'}
                          onChange={(e) => setSelectedChangeType(e.target.value as 'standard' | 'normal' | 'emergency')}
                          className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">Emergency Change</div>
                          <div className="text-xs text-gray-500 mt-1">Urgent changes that must be implemented immediately</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                      onClick={() => setShowChangeTypeModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                      onClick={() => convertToChangeMutation.mutate({
                        changeType: selectedChangeType,
                        responseBreachReason: conversionBreachModal.breachReasons.response,
                        ongoingBreachReason: conversionBreachModal.breachReasons.ongoing,
                        resolutionBreachReason: conversionBreachModal.breachReasons.resolution
                      })}
                      disabled={convertToChangeMutation.isPending}
                    >
                      {convertToChangeMutation.isPending ? 'Converting...' : 'Convert to Change'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Problem Conversion Modal */}
      <Transition appear show={showProblemModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowProblemModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 mb-4"
                  >
                    Convert to Problem
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-4">
                      Provide the following information to convert this case to a problem:
                    </p>
                    <div className="space-y-4">
                      {/* Problem Date */}
                      <div>
                        <label htmlFor="problemDate" className="block text-sm font-medium text-gray-700 mb-1">
                          Problem Date <span className="text-red-500">*</span>
                        </label>
                        <DateTimePickerInput
                          id="problemDate"
                          value={problemDate}
                          onChange={(val) => setProblemDate(val)}
                          showTimeSelect={false}
                          required
                          className="w-full"
                        />
                      </div>

                      {/* Reported By */}
                      <div>
                        <label htmlFor="reportedBy" className="block text-sm font-medium text-gray-700 mb-1">
                          Reported By <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="reportedBy"
                          value={reportedById}
                          onChange={(e) => setReportedById(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          required
                          disabled={isLoadingContacts}
                        >
                          <option value="">
                            {isLoadingContacts ? 'Loading contacts...' : '-- Select Contact --'}
                          </option>
                          {Array.isArray(contactsData?.users) && contactsData.users.map((user: any) => (
                            <option key={user.id} value={user.id}>
                              {user.firstName} {user.lastName} ({user.email})
                            </option>
                          ))}
                        </select>
                        {isLoadingContacts && (
                          <p className="mt-1 text-sm text-gray-500">Loading contacts from engagement...</p>
                        )}
                        {!isLoadingContacts && Array.isArray(contactsData?.users) && contactsData.users.length === 0 && (
                          <p className="mt-1 text-sm text-amber-600">No contacts found for this engagement. Please add contacts first.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                      onClick={() => setShowProblemModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                      onClick={() => convertToProblemMutation.mutate({
                        problemDate,
                        reportedById,
                        responseBreachReason: conversionBreachModal.breachReasons.response,
                        ongoingBreachReason: conversionBreachModal.breachReasons.ongoing,
                        resolutionBreachReason: conversionBreachModal.breachReasons.resolution
                      })}
                      disabled={convertToProblemMutation.isPending || !problemDate || !reportedById}
                    >
                      {convertToProblemMutation.isPending ? 'Converting...' : 'Convert to Problem'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Record Time Modal */}
      {caseData && caseData.Customer && caseData.Engagement && caseData.AssignmentGroup && (
        <RecordTimeModal
          isOpen={recordTimeModalOpen}
          onClose={() => setRecordTimeModalOpen(false)}
          onSuccess={() => {
            // Optionally refresh the case data after recording time
            queryClient.invalidateQueries({ queryKey: ['case', id] });
          }}
          ticketNumber={caseData.number || ''}
          customerName={caseData.Customer.accountName}
          customerId={caseData.customerId || caseData.Customer.id}
          engagementName={caseData.Engagement.name}
          engagementId={caseData.engagementId || caseData.Engagement.id}
          groupName={caseData.AssignmentGroup.name}
          groupId={caseData.assignmentGroupId || caseData.AssignmentGroup.id}
        />
      )}

      {/* Breach Reason Modal */}
      <BreachReasonModal
        isOpen={breachReasonModal.isOpen}
        onClose={() => setBreachReasonModal({ isOpen: false, targetType: undefined })}
        onConfirm={(reason, targetType) => {
          if (targetType) {
            breachReasonMutation.mutate({ breachReason: reason, targetType });
          }
        }}
        targetType={breachReasonModal.targetType}
        isLoading={breachReasonMutation.isPending}
      />

      {/* Conversion Breach Reason Modal */}
      <BreachReasonModal
        isOpen={conversionBreachModal.isOpen}
        onClose={() => {
          setConversionBreachModal({
            isOpen: false,
            conversionType: null,
            breachReasons: {},
            currentStep: null,
            stepsNeeded: []
          });
        }}
        onConfirm={(reason) => handleConversionBreachReasonSubmit(reason)}
        targetType={conversionBreachModal.currentStep || undefined}
        title={`Breach Reason Required for Conversion (${conversionBreachModal.stepsNeeded.indexOf(conversionBreachModal.currentStep!) + 1}/${conversionBreachModal.stepsNeeded.length})`}
        message={`Before converting this case to ${conversionBreachModal.conversionType?.replace('-', ' ')}, you must provide a breach reason for the ${conversionBreachModal.currentStep?.charAt(0)}${conversionBreachModal.currentStep?.slice(1).toLowerCase()} SLA that was breached.`}
        confirmLabel={conversionBreachModal.stepsNeeded.indexOf(conversionBreachModal.currentStep!) === conversionBreachModal.stepsNeeded.length - 1 ? 'Submit & Continue' : 'Next'}
        isLoading={convertToIncidentMutation.isPending || convertToProblemMutation.isPending || convertToChangeMutation.isPending || convertToServiceRequestMutation.isPending}
      />

      {/* Assignment Group Change Confirmation Modal */}
      <AssignmentGroupChangeConfirmModal
        isOpen={groupChangeConfirmModal.isOpen}
        onConfirm={handleConfirmGroupChange}
        onCancel={handleCancelGroupChange}
        newGroupName={groupChangeConfirmModal.newGroupName}
        ticketNumber={caseData?.number}
      />

    </div>
  );
};

export default CaseDetail;
